import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CBC_LEVELS, calculate8Level } from './cbcUtils';

/**
 * Generate a PDF from assignment description
 * @param {Object} assignment - Assignment object with title, description, due_date, total_marks
 */
export const generateAssignmentPDF = (assignment) => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Assignment', 20, 20);

    // Title
    doc.setFontSize(16);
    doc.text(assignment.title, 20, 35);

    // Details
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Due Date: ${new Date(assignment.due_date).toLocaleDateString()}`, 20, 45);
    doc.text(`Total Marks: ${assignment.total_marks}`, 20, 52);

    // Description
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Description:', 20, 65);

    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);

    // Split description into lines (max width 170)
    const lines = doc.splitTextToSize(assignment.description || 'No description provided', 170);
    doc.text(lines, 20, 75);

    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text('Generated by SchoolOS', 20, pageHeight - 10);

    // Save
    const filename = `${assignment.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
    doc.save(filename);
};

/**
 * Clean HTML tags and decode entities
 * @param {string} html 
 * @returns {string}
 */
const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html.replace(/<[^>]*>?/gm, ' '); // Replace tags with space first
    return txt.value;
};

/**
 * Clean unwanted spaces between letters in a string (e.g., "e x e e d i n g" -> "exceeding")
 * @param {string} str 
 * @returns {string}
 */
const cleanSpacing = (str) => {
    if (!str) return '';
    // If string has spaces between almost every letter
    const withoutSpaces = str.replace(/\s/g, '');
    if (str.length > 5 && str.includes(' ') && (str.length / withoutSpaces.length) > 1.5) {
        return withoutSpaces;
    }
    return str;
};

/**
 * Generate a PDF from lesson text content
 * @param {Object} content - Content object with title, body
 * @param {string} lessonTitle - Title of the lesson
 */
export const generateLessonContentPDF = (content, lessonTitle) => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Lesson Material', 20, 20);

    // Lesson Info
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Lesson: ${lessonTitle}`, 20, 30);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 36);

    // Content Title
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(content.title || 'Note', 20, 50);

    // Body
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');

    // Clean and decode text
    const cleanText = decodeHtml(content.body || '');
    const lines = doc.splitTextToSize(cleanText, 170);
    doc.text(lines, 20, 60);

    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text('Generated by Kianda School Management Portal', 20, pageHeight - 10);

    // Save
    const filename = `${(content.title || 'Lesson_Material').replace(/[^a-z0-9]/gi, '_')}.pdf`;
    doc.save(filename);
};

/**
 * Generate a PDF from a student's assignment submission
 * @param {Object} submission - Submission object
 * @param {Object} assignment - Assignment object
 */
export const generateSubmissionPDF = (submission, assignment) => {
    const doc = new jsPDF();

    // Header Color Stripe
    doc.setFillColor(24, 33, 109); // #18216D
    doc.rect(0, 0, 210, 15, 'F');

    // Header Title
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Submission Receipt', 20, 11);

    // Metadata Block
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Assignment: ${assignment.title}`, 20, 30);
    doc.text(`Scholar: ${submission.student_name} (${submission.student_id || 'N/A'})`, 20, 37);
    doc.text(`Learning Area: ${assignment.learning_area_name || 'General'}`, 20, 44);
    doc.text(`Submitted At: ${new Date(submission.submitted_at).toLocaleString()}`, 20, 51);

    // Section Divider
    doc.setDrawColor(24, 33, 109);
    doc.line(20, 60, 190, 60);

    let currentY = 75;

    // Submission Response
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(24, 33, 109);
    doc.text('Assessment Response:', 20, currentY);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(11);

    const responseText = submission.text_response || 'No text response provided.';
    const responseLines = doc.splitTextToSize(responseText, 170);
    doc.text(responseLines, 20, currentY + 7);

    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('This is an official submission record generated by the Kianda School Management System.', 20, pageHeight - 15);
    doc.text(`ID Reference: SUB-${submission.id}`, 20, pageHeight - 10);

    // Save
    const safeName = submission.student_name.replace(/[^a-z0-9]/gi, '_');
    const filename = `Submission_${safeName}_${assignment.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
    doc.save(filename);
};

/**
 * Generate a PDF for the student's Mastery Matrix (Success Hub)
 * @param {Object} reportData - The report data from the backend/SuccessHub
 */
export const generateMasteryMatrixPDF = (reportData) => {
    const doc = new jsPDF();
    const student = reportData.student || {};

    // Header Stripe
    doc.setFillColor(24, 33, 109); // #18216D
    doc.rect(0, 0, 210, 20, 'F');

    // Header Title
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Mastery Matrix Report', 20, 14);

    // Student Info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Scholar: ${student.full_name || student.name}`, 20, 30);
    doc.text(`Student ID: ${student.student_id || 'N/A'}`, 20, 36);
    doc.text(`Grade: ${student.grade_name || student.grade} ${student.stream_name || ''}`, 20, 42);
    doc.text(`Report Date: ${reportData.report_date || new Date().toLocaleDateString()}`, 150, 30);

    doc.setDrawColor(200, 200, 200);
    doc.line(20, 48, 190, 48);

    let currentY = 60;

    // Iterate through Learning Areas
    (reportData.learning_areas || []).forEach((area) => {
        // Check for page overflow
        if (currentY > 260) {
            doc.addPage();
            currentY = 20;
        }

        // Learning Area Title
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(24, 33, 109);
        doc.text(area.name, 20, currentY);
        const textWidth = doc.getTextWidth(area.name);
        doc.setLineWidth(0.5);
        doc.line(20, currentY + 1, 20 + textWidth, currentY + 1);
        currentY += 8;

        (area.strands || []).forEach((strand) => {
            (strand.sub_strands || []).forEach((sub) => {
                if (currentY > 270) {
                    doc.addPage();
                    currentY = 20;
                }

                // Sub-strand
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(60, 60, 60);
                doc.text(`Sub-Strand: ${sub.name}`, 25, currentY);
                currentY += 6;

                // Outcomes
                doc.setFontSize(9);
                doc.setFont(undefined, 'normal');
                doc.setTextColor(0, 0, 0);

                (sub.outcomes || []).forEach((outcome) => {
                    const outcomeText = `Outcome : ${outcome.description || outcome.outcome}`;
                    const lines = doc.splitTextToSize(outcomeText, 140);

                    if (currentY + (lines.length * 4) > 280) {
                        doc.addPage();
                        currentY = 20;
                    }

                    doc.text(lines, 30, currentY);

                    // Level
                    const level = outcome.competency_level || 'N/A';
                    doc.setFont(undefined, 'bold');
                    doc.text(level, 180, currentY);
                    doc.setFont(undefined, 'normal');

                    currentY += (lines.length * 4) + 2;
                });
                currentY += 4;
            });
        });
        currentY += 5;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128);
        doc.text(`Page ${i} of ${pageCount}`, 180, 285);
        doc.text('Kianda School Management Portal - Mastery Matrix', 20, 285);
    }

    doc.save(`Mastery_Matrix_${(student.full_name || 'Student').replace(/\s+/g, '_')}.pdf`);
};

// ─────────────────────────────────────────────────────────────────────────────
// Kianda School – Detailed Report Card (1-to-1 replica of the PDF sample)
// ─────────────────────────────────────────────────────────────────────────────

const BRAND_BLUE = [24, 33, 109];    // #18216D
const BRAND_GOLD = [180, 140, 40];
const LIGHT_GREY = [240, 240, 245];
const MED_GREY = [180, 180, 190];
const DARK_TEXT = [20, 20, 20];

const LEVEL_COLORS = {
    EE: CBC_LEVELS.find(l => l.code === 'EE').rgb,
    ME: CBC_LEVELS.find(l => l.code === 'ME').rgb,
    AE: CBC_LEVELS.find(l => l.code === 'AE').rgb,
    BE: CBC_LEVELS.find(l => l.code === 'BE').rgb,
};


/**
 * Draw a table cell with border, optional fill, and centred text.
 */
function drawCell(doc, x, y, w, h, text, opts = {}) {
    const {
        fill, textColor = DARK_TEXT, fontSize = 9, bold = false, align = 'center',
        border = true, borderColor = [200, 200, 210],
    } = opts;

    if (fill) { doc.setFillColor(...fill); doc.rect(x, y, w, h, 'F'); }
    if (border) {
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.3);
        doc.rect(x, y, w, h);
    }
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(...textColor);

    const lines = doc.splitTextToSize(String(text ?? ''), w - 4);
    const lineH = fontSize * 0.4;
    const totalH = lines.length * lineH;
    const ty = y + h / 2 - totalH / 2 + lineH;

    if (align === 'center') {
        lines.forEach((line, i) => doc.text(line, x + w / 2, ty + i * lineH, { align: 'center' }));
    } else if (align === 'right') {
        lines.forEach((line, i) => doc.text(line, x + w - 2, ty + i * lineH, { align: 'right' }));
    } else {
        lines.forEach((line, i) => doc.text(line, x + 2, ty + i * lineH));
    }
}

/**
 * Print a "PROVISIONAL" diagonal watermark across the current page.
 */
function drawProvisionalWatermark(doc) {
    doc.saveGraphicsState();
    doc.setGState(doc.GState({ opacity: 0.07 }));
    doc.setFontSize(52);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(24, 33, 109);
    doc.text('PROVISIONAL', 105, 148, { align: 'center', angle: 45 });
    doc.restoreGraphicsState();
}

/**
 * Draw the report card page header (school name, logo placeholder, report title).
 */
function drawHeader(doc, schoolName, termLabel, isProvisional, logoBase64, mainTitle = 'ASSESSMENT REPORT') {
    const W = doc.internal.pageSize.getWidth();

    // Logo (Left)
    if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', 14, 15, 24, 24, undefined, 'FAST');
    } else {
        // Fallback Logo Placeholder
        doc.setDrawColor(...BRAND_BLUE);
        doc.setLineWidth(0.5);
        doc.setFillColor(250, 250, 252);
        doc.roundedRect(14, 15, 20, 24, 2, 2, 'FD'); // Mock logo area

        // Draw shapes inside to look like a placeholder logo
        doc.setFillColor(...BRAND_BLUE);
        doc.rect(20, 20, 8, 8, 'F');
        doc.setFillColor(...BRAND_GOLD);
        doc.rect(20, 28, 8, 4, 'F');

        doc.setFontSize(6);
        doc.setTextColor(...BRAND_BLUE);
        doc.setFont('helvetica', 'bold');
        doc.text('LOGO', 24, 38, { align: 'center' });
    }

    // Right-aligned School Details
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(schoolName.toUpperCase(), W - 14, 18, { align: 'right' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('P.O.BOX 48328', W - 14, 24, { align: 'right' });
    doc.text('Kabarsiran Ave, Nairobi', W - 14, 28, { align: 'right' });
    doc.text('Tel: 0733846959', W - 14, 32, { align: 'right' });
    doc.text('Email: info@kiandaschool.ac.ke', W - 14, 36, { align: 'right' });

    // Top horizontal line (dashed)
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    if (doc.setLineDash) doc.setLineDash([2, 2], 0);
    doc.line(14, 45, W - 14, 45);
    if (doc.setLineDash) doc.setLineDash([], 0); // Reset dash

    if (mainTitle) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(mainTitle, W / 2, 53, { align: 'center' });

        // Add Date to far right
        const today = new Date().toLocaleDateString('en-GB');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(today, W - 14, 53, { align: 'right' });
    }

    // Bottom horizontal line (dashed)
    if (doc.setLineDash) doc.setLineDash([2, 2], 0);
    doc.line(14, 60, W - 14, 60);
    if (doc.setLineDash) doc.setLineDash([], 0);

    if (isProvisional) {
        doc.setFontSize(9);
        doc.setTextColor(200, 50, 50);
        doc.text('PROVISIONAL', 14, 53);
    }
}

/**
 * Draw the student particulars block.
 */
function drawStudentParticulars(doc, student, termLabel, yearLabel, startY) {
    let y = startY + 5;
    // Avatar Icon on the left
    const avatarCenterX = 26;
    const avatarCenterY = y + 10;

    doc.setFillColor(180, 180, 180); // Grey outline/body
    doc.circle(avatarCenterX, avatarCenterY, 12, 'F'); // Background silhouette base

    // Silhouette inner parts
    doc.setFillColor(255, 255, 255);
    // Head hole
    doc.circle(avatarCenterX, avatarCenterY - 3, 4.5, 'F');
    // Shoulders
    doc.ellipse(avatarCenterX, avatarCenterY + 8, 8, 5, 'F');

    // Two-column layout
    const col1X = 45;
    const col2X = 130;
    const textH = 7;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    // Row 1
    doc.setFont('helvetica', 'bold');
    doc.text('Name:', col1X, y + 5);
    doc.text('Academic Term:', col2X, y + 5);

    doc.setFont('helvetica', 'normal');
    doc.text(student.full_name || student.name || 'N/A', col1X + 13, y + 5);
    doc.text(termLabel, col2X + 28, y + 5);

    // Row 2
    doc.setFont('helvetica', 'bold');
    doc.text('Level:', col1X, y + 5 + textH);
    doc.text('Academic Year:', col2X, y + 5 + textH);

    doc.setFont('helvetica', 'normal');
    doc.text(`${student.grade_name || ''}${student.stream_name || ''}`, col1X + 11, y + 5 + textH);
    doc.text(yearLabel, col2X + 28, y + 5 + textH);
    // Row 3
    doc.setFont('helvetica', 'bold');
    doc.text('Adm No:', col1X, y + 5 + textH * 2);
    doc.text('Pathway:', col2X, y + 5 + textH * 2);

    doc.setFont('helvetica', 'normal');
    doc.text(student.student_id ? student.student_id.toString() : 'N/A', col1X + 16, y + 5 + textH * 2);
    doc.text(cleanPathway(student.pathway || 'Not Assigned'), col2X + 18, y + 5 + textH * 2);

    let newY = y + 5 + textH * 3 + 4;
    return newY;
}

function drawFormativeAssessmentBlocks(doc, learningAreas, summaryStats, student, startY) {
    const x0 = 14;
    const W = 186; // doc.internal.pageSize.getWidth() - 28
    const ROW_H = 8;
    let y = startY;

    // Section heading
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_BLUE);
    doc.text('FORMATIVE ASSESSMENT REPORT', x0, y);
    y += 5;

    learningAreas.forEach((area, idx) => {
        if (y > 250) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }

        const rawLvl = area.area_competency_level_8 || area.area_competency_level || 'N/A';
        const baseLvl = rawLvl.substring(0, 2).toUpperCase();
        const lvlColor = LEVEL_COLORS[baseLvl] || MED_GREY;

        const displayName = area.name === 'Mathematics' || area.name === 'Mathematics (Core or Essential)'
            ? (student.career_pathway === 'STEM' ? 'Core Mathematics' : 'Essential Mathematics')
            : area.name;

        // 1. Learning Area Subject Banner Header
        doc.setFillColor(...BRAND_BLUE);
        doc.rect(x0, y, W, ROW_H, 'F');

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(`${displayName.toUpperCase()}`, x0 + 4, y + 5.5);

        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.text(`Teacher: ${area.teacher_name || 'Not Assigned'}`, x0 + 90, y + 5.5);

        // Overall Subject Grade Badge on the right
        doc.setFont('helvetica', 'bold');
        doc.text('OVERALL GRADE:', x0 + W - 42, y + 5.5);

        doc.setFillColor(...lvlColor);
        doc.roundedRect(x0 + W - 18, y + 1.5, 16, 5, 1, 1, 'F');
        doc.setFontSize(7.5);
        doc.setTextColor(255, 255, 255);
        doc.text(cleanSpacing(rawLvl), x0 + W - 10, y + 5, { align: 'center' });

        y += ROW_H;

        if (area.strands && area.strands.length > 0) {
            // 2. Sub-table Headers: Strand / Topic | Sub-Strands Covered | Strand Performance
            const colW = [58, 92, 36];
            const subHeaders = ['STRAND / TOPIC', 'SUB-STRANDS COVERED', 'STRAND PERFORMANCE'];
            
            doc.setFillColor(240, 243, 248);
            doc.rect(x0, y, W, 6, 'F');
            doc.setDrawColor(220, 225, 235);
            doc.setLineWidth(0.2);
            doc.rect(x0, y, W, 6, 'S');

            let subXc = x0;
            subHeaders.forEach((h, i) => {
                doc.setFontSize(6.5);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(60, 70, 90);
                doc.text(h, subXc + (i === 2 ? colW[i] / 2 : 3), y + 4, { align: i === 2 ? 'center' : 'left' });
                subXc += colW[i];
            });
            y += 6;

            // 3. Render Strand Rows
            area.strands.forEach((strand, sIdx) => {
                if (y > 255) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }

                const strandLevel = strand.competency_level || 'N/A';
                const sBaseLvl = strandLevel.substring(0, 2).toUpperCase();
                const sLvlColor = LEVEL_COLORS[sBaseLvl] || [230, 230, 230];
                const sTextColor = strandLevel !== 'N/A' ? [255, 255, 255] : [60, 60, 60];

                // Prepare text lines for Strand Name and Sub-Strands
                doc.setFontSize(7.5);
                doc.setFont('helvetica', 'bold');
                const strandLines = doc.splitTextToSize(strand.name, colW[0] - 6);

                doc.setFontSize(7);
                doc.setFont('helvetica', 'normal');
                const subStrandNames = (strand.sub_strands || []).map(s => `• ${s.name}`);
                const subText = subStrandNames.length > 0 ? subStrandNames.join('\n') : '—';
                const subLines = doc.splitTextToSize(subText, colW[1] - 6);

                const rowH = Math.max(8, Math.max(strandLines.length * 3.5 + 4, subLines.length * 3.5 + 4));

                if (y + rowH > 265) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }

                const rowFill = sIdx % 2 === 0 ? [255, 255, 255] : [248, 250, 252];

                // Draw Row Outer Grid
                let sxc = x0;
                [colW[0], colW[1], colW[2]].forEach((w, i) => {
                    drawCell(doc, sxc, y, w, rowH, '', { fill: rowFill, borderColor: [225, 230, 240] });
                    sxc += w;
                });

                // Write Strand Name
                doc.setFontSize(7.5);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(30, 40, 60);
                doc.text(strandLines, x0 + 3, y + 4.5);

                // Write Sub-Strands
                doc.setFontSize(7);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(70, 80, 95);
                doc.text(subLines, x0 + colW[0] + 3, y + 4.5);

                // Draw Strand Performance Badge
                if (strandLevel !== 'N/A') {
                    doc.setFillColor(...sLvlColor);
                    doc.roundedRect(x0 + colW[0] + colW[1] + (colW[2] - 22) / 2, y + (rowH - 5) / 2, 22, 5, 1, 1, 'F');
                    doc.setFontSize(7);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(...sTextColor);
                    doc.text(cleanSpacing(strandLevel), x0 + colW[0] + colW[1] + colW[2] / 2, y + (rowH - 5) / 2 + 3.8, { align: 'center' });
                } else {
                    doc.setFontSize(7);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(150, 150, 150);
                    doc.text('—', x0 + colW[0] + colW[1] + colW[2] / 2, y + rowH / 2 + 1, { align: 'center' });
                }

                y += rowH;

                // 4. Embedded Rubric Card Block
                if (strand.assessment_rubric) {
                    if (y > 250) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }
                    const rubric = strand.assessment_rubric;
                    let rDesc = 'Not Graded';
                    if (strandLevel !== 'N/A') {
                        const rBaseLvl = strandLevel.substring(0, 2).toUpperCase();
                        const rDescKey = `criteria_${rBaseLvl.toLowerCase()}`;
                        rDesc = rubric[rDescKey] || 'No description available for this band.';
                    }

                    const boxX = x0 + 4;
                    const boxW = W - 8;

                    const rTitle = `Rubric: ${rubric.name}`;
                    const rGenDesc = rubric.description ? `Description: ${rubric.description}` : '';
                    const rAchieved = `Achieved Descriptor (${strandLevel}): ${rDesc}`;

                    doc.setFontSize(7.5);
                    const titleLines = doc.splitTextToSize(rTitle, boxW - 6);
                    doc.setFontSize(7);
                    const descLines = rGenDesc ? doc.splitTextToSize(rGenDesc, boxW - 6) : [];
                    const critLines = doc.splitTextToSize(rAchieved, boxW - 6);

                    const boxH = (titleLines.length * 3.5) + (descLines.length * 3.5) + (critLines.length * 3.5) + 6;

                    if (y + boxH > 265) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }

                    doc.setDrawColor(220, 225, 235);
                    doc.setLineWidth(0.2);
                    doc.setFillColor(252, 253, 255);
                    doc.roundedRect(boxX, y + 1.5, boxW, boxH, 1, 1, 'FD');

                    let ty = y + 5.5;
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(24, 33, 109);
                    doc.setFontSize(7.5);
                    doc.text(titleLines, boxX + 3, ty);
                    ty += titleLines.length * 3.5 + 1;

                    if (descLines.length > 0) {
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor(100, 100, 110);
                        doc.setFontSize(7);
                        doc.text(descLines, boxX + 3, ty);
                        ty += descLines.length * 3.5 + 1;
                    }

                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(40, 40, 50);
                    doc.setFontSize(7);
                    doc.text(critLines, boxX + 3, ty);

                    y += boxH + 4;
                }
            });
        }
        y += 4;
    });

    // OVERALL ROW
    if (y > 255) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }

    const overallCode = summaryStats?.average_performance_level_8 || summaryStats?.average_performance_level || 'N/A';
    const overallBase = overallCode.substring(0, 2).toUpperCase();
    const overallLvlColor = LEVEL_COLORS[overallBase] || MED_GREY;

    const summaryData = [
        { text: 'OVERALL FORMATIVE GRADE', opts: { fill: BRAND_BLUE, textColor: [255, 255, 255], bold: true, align: 'left', fontSize: 7.5 } },
        { text: cleanSpacing(overallCode), opts: { fill: overallLvlColor, textColor: [255, 255, 255], bold: true, fontSize: 8 } },
        { text: student.class_teacher_name || 'Not Assigned', opts: { fill: BRAND_BLUE, textColor: [255, 255, 255], bold: true, align: 'left', fontSize: 8 } },
    ];

    let xc = x0;
    const colW = [84, 18, 84];
    summaryData.forEach((cell, i) => {
        drawCell(doc, xc, y, colW[i], ROW_H, cell.text, cell.opts);
        xc += colW[i];
    });
    y += ROW_H;

    return y + 6;
}

/**
 * Draw the main Summative Assessment table.
 * Columns: Learning Area | Attendance | Average% | Level | Teacher
 */
function drawAssessmentTable(doc, learningAreas, attendancePct, summaryStats, student, startY, reportType = 'end_term') {
    if (reportType === 'formative') {
        return drawFormativeAssessmentBlocks(doc, learningAreas, summaryStats, student, startY);
    }

    const x0 = 14;
    const colW = [84, 18, 84]; // Learning Area | Level | Teacher (Total 186)
    const ROW_H = 8;
    let y = startY;

    // Section heading
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_BLUE);
    doc.text(reportType === 'formative' ? 'FORMATIVE ASSESSMENT' : 'SUMMATIVE ASSESSMENT', x0, y);
    y += 4;

    // Header row
    const headers = ['Learning Area', 'Level', 'Subject Teacher'];
    let xc = x0;
    headers.forEach((h, i) => {
        drawCell(doc, xc, y, colW[i], ROW_H, h, { fill: BRAND_BLUE, textColor: [255, 255, 255], bold: true, fontSize: 8 });
        xc += colW[i];
    });
    y += ROW_H;

    // Data rows
    learningAreas.forEach((area, idx) => {
        if (y > 265) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }

        // Handle 8-level codes for color mapping
        const rawLvl = area.area_competency_level_8 || area.area_competency_level || 'N/A';
        const baseLvl = rawLvl.substring(0, 2).toUpperCase(); // EE1 -> EE
        const lvlColor = LEVEL_COLORS[baseLvl] || MED_GREY;

        const rowFill = idx % 2 === 0 ? [255, 255, 255] : LIGHT_GREY;
        const cleanedLevel = cleanSpacing(rawLvl);

        const displayName = area.name === 'Mathematics' || area.name === 'Mathematics (Core or Essential)'
            ? (student.career_pathway === 'STEM' ? 'Core Mathematics' : 'Essential Mathematics')
            : area.name;

        xc = x0;
        const cellData = [
            { text: displayName, opts: { fill: rowFill, align: 'left', fontSize: 8 } },
            { text: cleanedLevel, opts: { fill: lvlColor, textColor: [255, 255, 255], bold: true, fontSize: 8 } },
            { text: area.teacher_name || '', opts: { fill: rowFill, align: 'left', fontSize: 8 } },
        ];

        cellData.forEach((cell, i) => {
            drawCell(doc, xc, y, colW[i], ROW_H, cell.text, cell.opts);
            xc += colW[i];
        });
        y += ROW_H;

        // For Formative reports, draw strands under each learning area
        if (reportType === 'formative' && area.strands && area.strands.length > 0) {
            area.strands.forEach(strand => {
                if (y > 265) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }
                
                // We use sub_strands to gather their competency levels since strand level isn't directly calculated at the parent level in report generator (it returns sub_strands->outcomes).
                // Or we just display the strand name and let sub_strands detail it, but the user requested strands.
                
                // Indented row for Strand
                const strandFill = [245, 248, 252];
                // Add LEVEL to Strand
                const strandLevel = strand.competency_level || 'N/A';
                const sLvlColor = LEVEL_COLORS[strandLevel] || strandFill;
                const sTextColor = strandLevel !== 'N/A' ? [255, 255, 255] : [0, 0, 0];


                let sxc = x0;
                const sCellData = [
                    { text: `   • ${strand.name}`, opts: { fill: strandFill, align: 'left', fontSize: 7, textColor: [50, 50, 70] } },
                    { text: strandLevel === 'N/A' ? '' : strandLevel, opts: { fill: sLvlColor, textColor: sTextColor, fontSize: 7, bold: strandLevel !== 'N/A' } },
                    { text: '', opts: { fill: strandFill, align: 'left', fontSize: 7 } },
                ];
                
                sCellData.forEach((cell, i) => {
                    drawCell(doc, sxc, y, colW[i], ROW_H - 1, cell.text, cell.opts);
                    sxc += colW[i];
                });
                y += ROW_H - 1;

                // Draw sub_strands directly instead of outcomes
                (strand.sub_strands || []).forEach(sub => {
                    if (y > 265) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }
                    
                    let ssc = x0;
                    const ssCellData = [
                        { text: `      - ${sub.name}`, opts: { fill: [255, 255, 255], align: 'left', fontSize: 6.5, textColor: [100, 100, 100] } },
                        { text: '', opts: { fill: [255, 255, 255], textColor: [255, 255, 255], fontSize: 6.5 } },
                        { text: '', opts: { fill: [255, 255, 255], align: 'left', fontSize: 6.5 } },
                    ];
                    
                    ssCellData.forEach((cell, i) => {
                        drawCell(doc, ssc, y, colW[i], ROW_H - 2, cell.text, cell.opts);
                        ssc += colW[i];
                    });
                    y += ROW_H - 2;
                });

                // Draw assessment rubric if available
                if (strand.assessment_rubric) {
                    if (y > 265) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }
                    
                    const rubric = strand.assessment_rubric;
                    let rDesc = 'Not Graded';
                    if (strandLevel !== 'N/A') {
                        const rBaseLvl = strandLevel.substring(0, 2).toUpperCase();
                        const rDescKey = `criteria_${rBaseLvl.toLowerCase()}`;
                        rDesc = rubric[rDescKey] || 'No description available for this band.';
                    }
                    
                    const rubricColW = [50, 106, 30]; // Total 186
                    const splitDesc = doc.splitTextToSize(rDesc, rubricColW[1] - 4);
                    const rRowH = Math.max(ROW_H - 1, splitDesc.length * 3 + 2);

                    let rxc = x0;
                    const rCellData = [
                        { text: `      [Rubric] ${rubric.name}`, opts: { fill: [252, 252, 252], align: 'left', fontSize: 6.5, textColor: [24, 33, 109], bold: true } },
                        { text: splitDesc, opts: { fill: [252, 252, 252], align: 'left', fontSize: 6, textColor: [80, 80, 80] } },
                        { text: strandLevel, opts: { fill: [252, 252, 252], textColor: [24, 33, 109], fontSize: 6.5, bold: true } },
                    ];
                    
                    rCellData.forEach((cell, i) => {
                        drawCell(doc, rxc, y, rubricColW[i], rRowH, cell.text, cell.opts);
                        rxc += rubricColW[i];
                    });
                    y += rRowH;
                }
            });
        }
    });

    // OVERALL ROW
    if (y > 265) { doc.addPage(); drawProvisionalWatermark(doc); y = 20; }

    const overallCode = summaryStats?.average_performance_level_8 || summaryStats?.average_performance_level || 'N/A';
    const overallBase = overallCode.substring(0, 2).toUpperCase();
    const overallLvlColor = LEVEL_COLORS[overallBase] || MED_GREY;

    xc = x0;
    const summaryData = [
        { text: 'OVERALL GRADE', opts: { fill: BRAND_BLUE, textColor: [255, 255, 255], bold: true, align: 'left', fontSize: 7.5 } },
        { text: cleanSpacing(overallCode), opts: { fill: overallLvlColor, textColor: [255, 255, 255], bold: true, fontSize: 8 } },
        { text: student.class_teacher_name || 'Not Assigned', opts: { fill: BRAND_BLUE, textColor: [255, 255, 255], bold: true, align: 'left', fontSize: 8 } },
    ];

    summaryData.forEach((cell, i) => {
        drawCell(doc, xc, y, colW[i], ROW_H, cell.text, cell.opts);
        xc += colW[i];
    });
    y += ROW_H;

    return y + 6;
}

/**
 * Draw the rating scale legend.
 */
function drawRatingScale(doc, startY) {
    let y = startY;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_BLUE);
    doc.setFontSize(8);
    doc.text('Performance Scale', 14, y);
    y += 6;

    const items = [
        { code: 'EE1', label: 'Exceeding expectations', range: '(90% - 100%)', color: LEVEL_COLORS.EE },
        { code: 'EE2', label: 'Exceeding expectations', range: '(75% - 89.9%)', color: LEVEL_COLORS.EE },
        { code: 'ME1', label: 'Meeting expectations', range: '(65% - 74.9%)', color: LEVEL_COLORS.ME },
        { code: 'ME2', label: 'Meeting expectations', range: '(50% - 64.9%)', color: LEVEL_COLORS.ME },
        { code: 'AE1', label: 'Approaching expectations', range: '(40% - 49.9%)', color: LEVEL_COLORS.AE },
        { code: 'AE2', label: 'Approaching expectations', range: '(30% - 39.9%)', color: LEVEL_COLORS.AE },
        { code: 'BE1', label: 'Below expectations', range: '(20% - 29.9%)', color: LEVEL_COLORS.BE },
        { code: 'BE2', label: 'Below expectations', range: '(0% - 19.9%)', color: LEVEL_COLORS.BE },
    ];

    const colWidth = 91; // (182 / 2)
    const rowHeight = 8;

    for (let i = 0; i < 4; i++) {
        // Column 1
        const item1 = items[i];
        drawCell(doc, 14, y, 10, rowHeight, item1.code, { fill: item1.color, textColor: [255, 255, 255], bold: true, fontSize: 7 });
        drawCell(doc, 24, y, 35, rowHeight, item1.label, { align: 'left', fontSize: 6.5, borderColor: [230, 230, 235] });
        drawCell(doc, 59, y, 32, rowHeight, item1.range, { align: 'left', fontSize: 6.5, borderColor: [230, 230, 235] });

        // Column 2
        const item2 = items[i + 4];
        drawCell(doc, 14 + colWidth, y, 10, rowHeight, item2.code, { fill: item2.color, textColor: [255, 255, 255], bold: true, fontSize: 7 });
        drawCell(doc, 24 + colWidth, y, 35, rowHeight, item2.label, { align: 'left', fontSize: 6.5, borderColor: [230, 230, 235] });
        drawCell(doc, 59 + colWidth, y, 32, rowHeight, item2.range, { align: 'left', fontSize: 6.5, borderColor: [230, 230, 235] });

        y += rowHeight;
    }

    return y + 5;
}

/**
 * Draw the deportment table and its overall average.
 */

/**
 * Draw a narrative text block (Core Competencies or Values).
 */
function drawNarrativeBlock(doc, title, text, startY, width = 182, x = 14) {
    if (!text) return startY;
    let y = startY;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_BLUE);
    doc.text(title, x, y);
    y += 4;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...DARK_TEXT);
    const lines = doc.splitTextToSize(text, width - 4); // 2mm padding each side
    const blockHeight = lines.length * 4 + 4;

    // Draw Box
    doc.setDrawColor(200, 200, 210);
    doc.setLineWidth(0.2);
    doc.rect(x, y - 1, width, blockHeight, 'S');

    doc.text(lines, x + 2, y + 3); // Padding in the box
    return y + blockHeight + 6;
}

/**
 * Utility to clean pathway description
 */
function cleanPathway(pathway) {
    if (!pathway) return 'N/A';
    return pathway.split('(')[0].trim();
}

/**
 * Draw teacher and headteacher comment blocks.
 */
// Signatures removed as per user request

/**
 * Add page numbers and footer on every page.
 */
function addFooters(doc, isProvisional) {
    const n = doc.internal.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(140, 140, 150);
        doc.text('Powered by SchoolOS – Kianda School Management Portal', 14, 290);
        doc.text(`Page ${i} of ${n}`, 196, 290, { align: 'right' });
        if (isProvisional) drawProvisionalWatermark(doc);
    }
}

export const getBase64ImageFromUrl = async (imageUrl) => {
    try {
        const res = await fetch(imageUrl);
        if (!res.ok) return null;
        const blob = await res.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        console.warn("Could not load logo", e);
        return null;
    }
};

/**
 * Main export: Generate the Kianda School detailed report card PDF.
 * 
 * @param {Object} reportData – Full API response from /api/cbc/reports/student/<id>/?term=<term_id>
 * @param {Object} meta       – { schoolName, termLabel, yearLabel, returnType: 'save' | 'blob' }
 * @returns {Promise<Blob|void>} Returns a Blob if returnType is 'blob', otherwise saves the file.
 */
export const generateDetailedReportPDF = async (reportData, meta = {}) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    let logoBase64 = await getBase64ImageFromUrl('/kianda-school-logo-removebg-preview.png');

    const student = reportData.student || {};
    const termReport = reportData.term_report || null;
    const areas = reportData.learning_areas || [];
    const attendPct = reportData.summary_stats?.attendance_percentage ?? null;

    const schoolName = meta.schoolName || 'Kianda School';
    const termLabel = meta.termLabel || reportData.term_label || 'Term';
    const yearLabel = meta.yearLabel || reportData.year_label || new Date().getFullYear().toString();
    const reportType = meta.reportType || 'end_term';
    const isMidTerm = meta.reportPeriod === 'mid_term' || meta.reportPeriod === 'Mid Term Report';
    const isProvisional = !isMidTerm && reportType !== 'formative' && (!termReport || termReport.status !== 'FINAL');

    let mainTitle = 'END TERM REPORT';
    if (isMidTerm) mainTitle = 'MID TERM REPORT';
    if (reportType === 'formative') mainTitle = 'FORMATIVE ASSESSMENT REPORT';

    // ── PAGE 1 ──────────────────────────────────────────────────────────────
    drawHeader(doc, schoolName, termLabel, isProvisional, logoBase64, mainTitle);

    let y = drawStudentParticulars(doc, student, termLabel, yearLabel, 60);
    y = drawAssessmentTable(doc, areas, attendPct, reportData.summary_stats, student, y, reportType);
    y = drawRatingScale(doc, y);

    if (isMidTerm || reportType === 'formative') {
        addFooters(doc, isProvisional);
        const safeName = (student.name || student.full_name || 'Student').replace(/\s+/g, '_');
        if (meta.returnType === 'blob') return doc.output('blob');
        doc.save(`Report_Card_${safeName}.pdf`);
        return;
    }

    // ── PAGE 2 ──────────────────────────────────────────────────────────────
    doc.addPage();
    drawHeader(doc, schoolName, termLabel, isProvisional, logoBase64);
    y = 65;

    // Overall Deportment & Attendance Summary Row
    const absentDays = termReport?.absent_days ?? reportData?.summary_stats?.absent_days ?? '—';
    const deportmentGrade = termReport?.deportment_grade || 'N/A';
    const W = doc.internal.pageSize.getWidth();

    if (!isMidTerm) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...BRAND_BLUE);
        doc.text(`OVERALL DEPORTMENT: ${deportmentGrade}`, 14, y);
        doc.text(`DAYS ABSENT: ${absentDays}`, W - 14, y, { align: 'right' });
        y += 10;
    }

    // Core Competencies & Values Side-by-Side
    const selectedCompetencies = termReport?.core_competencies || [];
    const selectedValues = termReport?.values || [];

    if (selectedCompetencies.length || selectedValues.length) {
        const narrativeY = y;
        let leftY = narrativeY;
        let rightY = narrativeY;

        if (selectedCompetencies.length) {
            leftY = drawNarrativeBlock(
                doc,
                'CORE COMPETENCIES',
                selectedCompetencies.join(', '),
                narrativeY,
                89, // Width
                14  // X
            );
        }

        if (selectedValues.length) {
            rightY = drawNarrativeBlock(
                doc,
                'VALUES',
                selectedValues.join(', '),
                narrativeY,
                89,  // Width
                107  // X (14 + 89 + 4 padding)
            );
        }
        y = Math.max(leftY, rightY);
    }


    // Responsibilities
    if (termReport?.responsibilities) {
        y = drawNarrativeBlock(doc, 'RESPONSIBILITIES', termReport.responsibilities, y);
    }

    // Social Work
    if (!isMidTerm && termReport?.social_work_hours) {
        y = drawNarrativeBlock(doc, 'SOCIAL WORK HOURS', `${termReport.social_work_hours} Hours`, y);
    }

    // Co-curricular Activities
    if (termReport?.cocurricular_activities) {
        y = drawNarrativeBlock(doc, 'CO-CURRICULAR ACTIVITIES', termReport.cocurricular_activities, y);
    }

    // Overall Deportment display at bottom removed (moved to top)

    // ── Footers on all pages ─────────────────────────────────────────────────
    addFooters(doc, isProvisional);

    const safeName = (student.full_name || 'Student').replace(/\s+/g, '_');

    if (meta.returnType === 'blob') {
        return doc.output('blob');
    }

    doc.save(`Report_Card_${safeName}.pdf`);
};

/**
 * Generate a high-density performance matrix for a whole class (Landscape)
 * @param {Object} matrixData - Data from /performance_matrix/ endpoint
 */
export const generatePerformanceMatrixPDF = async (matrixData) => {
    // ── Auto-select page format based on subject count ────────────────────────
    // A4 landscape (297mm) fits up to 6 subjects comfortably.
    // A3 landscape (420mm) is used for 7+ subjects for full-width, uncompressed rendering.
    const subjectCount = (matrixData.subjects || []).length;
    const useA3 = subjectCount > 6;
    const pageFormat = useA3 ? 'a3' : 'a4';

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: pageFormat });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    let logoBase64 = await getBase64ImageFromUrl('/kianda-school-logo-removebg-preview.png');

    const { subjects, class_info } = matrixData;
    // Alphabetical sort of students
    const sortedStudents = [...matrixData.students].sort((a, b) =>
        (a.full_name || a.name || '').localeCompare(b.full_name || b.name || '')
    );
    const timestamp = new Date().toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });

    // ── DATA ENRICHMENT: Calculate Summaries ───────────────────────────────

    // 1. Student Averages (Row-wise)
    const studentsWithAvg = sortedStudents.map(s => {
        const scores = Object.values(s.subject_scores)
            .filter(score => score && score.percentage !== null)
            .map(score => score.percentage);

        const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
        return {
            ...s,
            overall_avg_pct: avg,
            overall_avg_level: calculate8Level(avg)
        };
    });

    // 2. Subject Averages (Column-wise)
    const subjectAverages = subjects.map(sub => {
        const scores = sortedStudents
            .map(s => s.subject_scores[sub.code])
            .filter(score => score && score.percentage !== null)
            .map(score => score.percentage);

        const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
        return {
            code: sub.code,
            avg_pct: avg,
            avg_level: calculate8Level(avg)
        };
    });

    // ── HORIZONTAL SPLITTING (Subject Chunking) ───────────────────────────
    // With A3: fit up to 12 subjects per page — no compression needed.
    // With A4: max 6 per page for comfortable rendering.
    const nameColWidth = useA3 ? 60 : 55;
    const summaryColWidth = useA3 ? 32 : 28;
    const marginTotal = 28; // 14mm each side
    const availableWidth = W - marginTotal - nameColWidth - summaryColWidth;
    const MAX_SUBJECTS_PER_PAGE = useA3 ? 12 : 6;
    const targetSubjectWidth = Math.max(28, Math.min(38, availableWidth / Math.min(subjects.length, MAX_SUBJECTS_PER_PAGE)));

    const subjectChunks = [];
    for (let i = 0; i < subjects.length; i += MAX_SUBJECTS_PER_PAGE) {
        subjectChunks.push(subjects.slice(i, i + MAX_SUBJECTS_PER_PAGE));
    }

    subjectChunks.forEach((chunk, chunkIdx) => {
        if (chunkIdx > 0) doc.addPage();

        // 1. Header Logic
        drawHeader(doc, 'KIANDA SCHOOL', `${class_info.term || 'Term'} Performance Matrix`, false, logoBase64, ''); // Removed ASSESSMENT REPORT

        // 4. Matrix Branding Helper (Repeated on every page)
        const drawMatrixBranding = (pageDoc, currentChunkIdx, totalChunks) => {
            pageDoc.setFontSize(10.5);
            pageDoc.setFont('helvetica', 'bold');
            pageDoc.setTextColor(...BRAND_BLUE);
            const classLabel = `Class: ${class_info.grade} - ${class_info.stream || ''}`;
            pageDoc.text(classLabel, 14, 53);

            pageDoc.setFontSize(9);
            pageDoc.setFont('helvetica', 'normal');
            pageDoc.text(`Part: ${currentChunkIdx + 1} of ${totalChunks}`, W / 2, 58, { align: 'center' });
            pageDoc.text(`Generated: ${timestamp}`, W - 14, 53, { align: 'right' });

            // Decorative separation lines
            pageDoc.setDrawColor(220, 220, 220);
            pageDoc.line(14, 48, W - 14, 48);
            pageDoc.line(14, 62, W - 14, 62);
        };

        drawMatrixBranding(doc, chunkIdx, subjectChunks.length);

        // 2. Table Configuration
        const startY = 65;
        const subjectColWidth = chunk.length < MAX_SUBJECTS_PER_PAGE ? targetSubjectWidth : availableWidth / chunk.length;
        const rowHeight = 8;
        let y = startY;

        // 3. Grid Header
        drawCell(doc, 14, y, nameColWidth, rowHeight, 'SCHOLAR NAME', { fill: BRAND_BLUE, textColor: [255, 255, 255], bold: true, align: 'left', fontSize: 8 });

        chunk.forEach((sub, i) => {
            const x = 14 + nameColWidth + (i * subjectColWidth);
            const subTitle = sub.name.length > 20 ? sub.code : sub.name;
            drawCell(doc, x, y, subjectColWidth, rowHeight, subTitle.toUpperCase(), {
                fill: BRAND_BLUE,
                textColor: [255, 255, 255],
                bold: true,
                fontSize: 6.5 // Smaller font for high density
            });
        });

        // Add Summary Column Header on the last chunk
        if (chunkIdx === subjectChunks.length - 1) {
            drawCell(doc, 14 + nameColWidth + (chunk.length * subjectColWidth), y, summaryColWidth, rowHeight, 'OVERALL AVG', { fill: BRAND_GOLD, textColor: [255, 255, 255], bold: true, fontSize: 7 });
        }

        y += rowHeight;

        // 4. Grid Data
        studentsWithAvg.forEach((student, sIdx) => {
            // Page Break (Vertical)
            if (y > H - 60) { // Leave room for footer/legend
                addFooters(doc, false);
                doc.addPage();
                drawHeader(doc, 'KIANDA SCHOOL', `${class_info.term} Performance Matrix`, false, logoBase64, ''); // Suppress title
                drawMatrixBranding(doc, chunkIdx, subjectChunks.length);
                y = 75; // Increased start Y to clear branding
                // Redraw Header Row
                drawCell(doc, 14, y, nameColWidth, rowHeight, 'SCHOLAR NAME', { fill: BRAND_BLUE, textColor: [255, 255, 255], bold: true, align: 'left', fontSize: 8 });
                chunk.forEach((sub, i) => {
                    const x = 14 + nameColWidth + (i * subjectColWidth);
                    const subTitle = sub.name.length > 20 ? sub.code : sub.name;
                    drawCell(doc, x, y, subjectColWidth, rowHeight, subTitle.toUpperCase(), {
                        fill: BRAND_BLUE,
                        textColor: [255, 255, 255],
                        bold: true,
                        fontSize: 6.5
                    });
                });
                if (chunkIdx === subjectChunks.length - 1) {
                    drawCell(doc, 14 + nameColWidth + (chunk.length * subjectColWidth), y, summaryColWidth, rowHeight, 'OVERALL AVG', { fill: BRAND_GOLD, textColor: [255, 255, 255], bold: true, fontSize: 7 });
                }
                y += rowHeight;
            }

            const rowFill = sIdx % 2 === 0 ? [255, 255, 255] : LIGHT_GREY;

            // Name Cell
            drawCell(doc, 14, y, nameColWidth, rowHeight, student.name.toUpperCase(), { fill: rowFill, align: 'left', fontSize: 7, bold: true });

            // Subject Cells
            chunk.forEach((sub, i) => {
                const x = 14 + nameColWidth + (i * subjectColWidth);
                const score = student.subject_scores[sub.code];

                if (score && score.level !== 'N/A') {
                    const baseLvl = score.level.substring(0, 2).toUpperCase();
                    const color = LEVEL_COLORS[baseLvl] || DARK_TEXT;
                    const scoreText = `${Math.round(score.percentage)}% ${score.level}`;

                    drawCell(doc, x, y, subjectColWidth, rowHeight, scoreText, {
                        fill: color,
                        textColor: [255, 255, 255],
                        bold: true,
                        fontSize: 7
                    });
                } else {
                    drawCell(doc, x, y, subjectColWidth, rowHeight, '-', { fill: rowFill, textColor: [200, 200, 200], fontSize: 8 });
                }
            });

            // Summary Column Data
            if (chunkIdx === subjectChunks.length - 1) {
                const avgText = `${Math.round(student.overall_avg_pct)}% ${student.overall_avg_level}`;
                const avgColor = LEVEL_COLORS[student.overall_avg_level.substring(0, 2)] || BRAND_BLUE;
                drawCell(doc, 14 + nameColWidth + (chunk.length * subjectColWidth), y, summaryColWidth, rowHeight, avgText, { fill: avgColor, textColor: [255, 255, 255], bold: true, fontSize: 7 });
            }

            y += rowHeight;
        });

        // 5. Summary Row (Vertical Footer for Subjects)
        drawCell(doc, 14, y, nameColWidth, rowHeight, 'SUBJECT MEAN', { fill: BRAND_BLUE, textColor: [255, 255, 255], bold: true, align: 'left', fontSize: 7.5 });
        chunk.forEach((sub, i) => {
            const x = 14 + nameColWidth + (i * subjectColWidth);
            const subAvg = subjectAverages.find(sa => sa.code === sub.code);
            const avgText = subAvg ? `${Math.round(subAvg.avg_pct)}% ${subAvg.avg_level}` : 'N/A';

            drawCell(doc, x, y, subjectColWidth, rowHeight, avgText, { fill: BRAND_BLUE, textColor: [255, 255, 255], bold: true, fontSize: 6.5 });
        });

        if (chunkIdx === subjectChunks.length - 1) {
            drawCell(doc, 14 + nameColWidth + (chunk.length * subjectColWidth), y, summaryColWidth, rowHeight, '', { fill: BRAND_BLUE });
        }

        y += rowHeight + 10;

        // 6. Legend & Signatures (Only on the last page of the last chunk if possible, or repeated)
        if (chunkIdx === subjectChunks.length - 1) {
            if (y > H - 50) {
                addFooters(doc, false);
                doc.addPage();
                drawHeader(doc, 'KIANDA SCHOOL', `Performance Matrix Legend`, false, logoBase64);
                y = 65;
            }
            y = drawRatingScale(doc, y);
        }

        addFooters(doc, false); // Watermark is omitted in addFooters if isProvisional is false
    });

    doc.save(`Performance_Matrix_${class_info.grade}_${timestamp.replace(/\//g, '-')}.pdf`);
};

/**
 * Generate a PDF receipt from the Parent Dashboard Account Statements.
 */
export const generateReceiptPDF = async (payment, student) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    let logoBase64 = null;
    try {
        logoBase64 = await getBase64ImageFromUrl('/kianda-school-logo-removebg-preview.png');
    } catch (e) {
        console.warn("Could not load logo for receipt");
    }

    // ── Header (Customized drawHeader with updated email) ──
    if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', 14, 15, 24, 24, undefined, 'FAST');
    }

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('KIANDA SCHOOL', W - 14, 18, { align: 'right' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('P.O.BOX 48328', W - 14, 24, { align: 'right' });
    doc.text('Kabarsiran Ave, Nairobi', W - 14, 28, { align: 'right' });
    doc.text('Tel: 0733846959', W - 14, 32, { align: 'right' });
    doc.text('Email: Accounts@kiandaschool.ac.ke', W - 14, 36, { align: 'right' });

    // Top horizontal line (dashed)
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    if (doc.setLineDash) doc.setLineDash([2, 2], 0);
    doc.line(14, 45, W - 14, 45);
    if (doc.setLineDash) doc.setLineDash([], 0); // Reset dash

    // ── Student & Receipt Details ──
    let y = 55;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    const leftColX = 14;
    const leftValX = 40;
    const rightColX = W / 2 + 10;
    const rightValX = rightColX + 35;

    // Row 1
    doc.setFont('helvetica', 'bold');
    doc.text('Name:', leftColX, y);
    doc.text('Receipt No.:', rightColX, y);
    doc.setFont('helvetica', 'normal');
    const fullName = `${student?.first_name || ''} ${student?.last_name || ''}`.trim() || 'N/A';
    doc.text(fullName.toUpperCase(), leftValX, y);
    doc.text(payment?.transaction_reference || 'N/A', rightValX, y);
    y += 6;

    // Row 2
    doc.setFont('helvetica', 'bold');
    doc.text('Admin. No:', leftColX, y);
    doc.text('Date:', rightColX, y);
    doc.setFont('helvetica', 'normal');
    doc.text((student?.student_id || 'N/A').toString(), leftValX, y);
    const dateStr = payment?.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-GB') : 'N/A';
    doc.text(dateStr, rightValX, y);
    y += 6;

    // Row 3
    doc.setFont('helvetica', 'bold');
    doc.text('Class:', leftColX, y);
    doc.text('Mode of Payment', rightColX, y);
    doc.setFont('helvetica', 'normal');
    doc.text((student?.grade_name || student?.grade || 'N/A').toUpperCase(), leftValX, y);
    doc.text('Bank/M-Pesa', rightValX, y);
    y += 6;

    // Row 4
    doc.setFont('helvetica', 'bold');
    doc.text('Stream:', leftColX, y);
    doc.text('Transaction No.', rightColX, y);
    doc.setFont('helvetica', 'normal');
    doc.text((student?.stream_name || 'N/A').toUpperCase(), leftValX, y);
    doc.text(payment?.external_no || 'N/A', rightValX, y);
    y += 12;

    // ── Transaction Table ──
    const amountStr = new Intl.NumberFormat('en-KE', { style: 'decimal', minimumFractionDigits: 2 }).format(payment?.amount || 0);

    autoTable(doc, {
        startY: y,
        head: [['S/N', 'Description', 'Amount']],
        body: [
            ['1', payment?.description || '', amountStr]
        ],
        theme: 'grid',
        headStyles: {
            fillColor: [245, 245, 245],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 9,
            lineWidth: 0.1,
            lineColor: [200, 200, 200]
        },
        bodyStyles: {
            textColor: [0, 0, 0],
            fontSize: 9,
            lineWidth: 0.1,
            lineColor: [200, 200, 200]
        },
        columnStyles: {
            0: { cellWidth: 20 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 40 }
        },
        margin: { left: 14, right: 14 }
    });

    y = doc.lastAutoTable.finalY + 15;

    // ── Footer Section ──
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Method', 14, y);
    y += 6;

    const fLeftColX = 14;
    const fRightColX = W / 2 + 10;
    doc.setFontSize(9);

    // Bank Details (Left)
    doc.text('Bank Payment Details', fLeftColX, y);
    doc.line(fLeftColX, y + 1, fLeftColX + 32, y + 1); // Manual underline
    doc.setFont('helvetica', 'normal');
    doc.text('Bank Name: NCBA', fLeftColX, y + 5);
    doc.text('Account Name: KIANDA FOUNDATION TRUST', fLeftColX, y + 10);
    doc.text('Account No: 6434350018', fLeftColX, y + 15);
    doc.text('Branch: Westlands', fLeftColX, y + 20);
    doc.text('Swift Code: CBA FKENX', fLeftColX, y + 25);
    doc.text('Branch Code: 004', fLeftColX, y + 30);
    doc.text('Reference: Student\'s Account Number', fLeftColX, y + 35);
    doc.text('Currency: KSH (shilling)', fLeftColX, y + 40);

    // MPESA Details (Right)
    doc.setFont('helvetica', 'bold');
    doc.text('MPESA Payment Details', fRightColX, y);
    doc.line(fRightColX, y + 1, fRightColX + 35, y + 1); // Manual underline
    doc.setFont('helvetica', 'normal');
    doc.text('Paybill No: 34104', fRightColX, y + 5);
    doc.text('Account No: Student\'s Account Number', fRightColX, y + 10);

    y += 55;

    doc.setFont('helvetica', 'bold');
    doc.text('NB: FEES ONCE PAID IS NOT REFUNDABLE.', 14, y);

    y += 15;
    doc.text('THANK YOU!', W / 2, y, { align: 'center' });

    doc.save(`Receipt_${payment?.transaction_reference || 'N_A'}.pdf`);
};
